// Recent tickets, split into Customer / Store / Brand — the pure half.
//
// The desk says "Store" and "Brand"; the schema says `locations` and
// `organizations` (migration 004's header: "Locations (stores) under
// organizations"). This module is the only place that mapping is written down in
// the front end, and it is pure so `node --test` can reach it — there is no DOM
// harness in this repository (test/web/queue-query.test.js says the same thing).
//
// ---------------------------------------------------------------------------
// Why ONE request, not three
// ---------------------------------------------------------------------------
// `GET /api/tickets` (docs/API.md §3.1) filters on `organization_id`. It does NOT
// filter on `customer_id` or `location_id` — not in the route's `validateQuery`
// allow-list and, decisively, not in `buildFilters` in src/services/tickets.js,
// which reads exactly publicRef, q, status, assigned_user_id, queue_id,
// organization_id and channel. Adding the two names to the route's allow-list
// alone would be WORSE than leaving it alone: the validator would accept
// `?customer_id=7`, the service would ignore it, and the endpoint would return
// every ticket the caller can see — which this panel would then render under a
// header reading "Customer". A filter that is accepted and silently dropped is
// the exact failure this codebase keeps writing comments about.
//
// So: one organization-scoped fetch, grouped here. The failure mode that buys is
// truncation — the customer's own tickets can fall off the end of the brand's
// page — and that is handled honestly rather than hidden, see `count` below.
//
// ---------------------------------------------------------------------------
// Nested, not mutually exclusive
// ---------------------------------------------------------------------------
// A ticket raised by this customer, at this store, for this brand appears in all
// three groups. These are three widening SCOPES, not three categories:
//
//   * "Store" has to answer "what is happening at this location", and a ticket
//     from the person on the phone is happening at that location. Removing it
//     because a narrower group also claims it makes the Store list a wrong
//     answer to the only question it is asked.
//   * Exclusive grouping would redefine Brand as "the brand, excluding this
//     store", whose count means nothing to anybody.
//   * It keeps the Brand count honest for free: it is the server's own COUNT of
//     brand tickets (`page.total`), not a number this module derived.
//
// The cost is seeing a ticket twice, which is why each group header carries the
// name of the thing it is scoped to (this customer, this store, this brand)
// rather than the bare word — the duplication then reads as drill-down instead
// of as a bug. Brand is collapsed by default, so in the common case the
// duplicate is not even on screen.

/** Store = `locations`, Brand = `organizations`. Order is the order rendered. */
export const RECENT_GROUP_KEYS = ['customer', 'store', 'brand'];

/**
 * Which groups start open. The developer asked for exactly this: "have customer
 * and store already be expanded with Brand default collapsed". Brand is the
 * widest and least often the answer, and it is the one that can be long.
 */
export const DEFAULT_EXPANDED = Object.freeze({
  customer: true,
  store: true,
  brand: false,
});

/** A fresh mutable copy for `useState` — the frozen constant must not be handed to React. */
export function initialExpanded() {
  return { ...DEFAULT_EXPANDED };
}

/** How many tickets each group lists. The count in the header tells the real size. */
export const PER_GROUP_LIMIT = 5;

const asId = (value) => (value === null || value === undefined ? null : String(value));

export function groupRecentTickets({
  rows = [],
  total = null,
  customer = null,
  location = null,
  organization = null,
  perGroup = PER_GROUP_LIMIT,
} = {}) {
  const customerId = asId(customer?.id);
  const locationId = asId(location?.id);
  const organizationId = asId(organization?.id);

  const sample = Array.isArray(rows) ? rows : [];
  // `page.total` is a real COUNT over the filters, ignoring limit/offset
  // (src/lib/pagination.js). When it exceeds what we were given, older brand
  // tickets exist that this sample never saw — so any count DERIVED from the
  // sample is a lower bound, not a count, and this module returns null instead.
  const brandTotal = Number.isFinite(Number(total)) && total !== null ? Number(total) : sample.length;
  const truncated = brandTotal > sample.length;

  const groups = [
    {
      key: 'customer',
      label: 'Customer',
      scopeName: customer?.name ?? null,
      // No customer chosen yet: the agent opened the screen and picked an
      // organization first. Nothing to match on — say so, do not show an
      // empty box that reads as "this person has never called".
      unavailableReason: customerId ? null : 'No caller identified yet.',
      matches: customerId ? sample.filter((t) => asId(t?.customer?.id) === customerId) : [],
      // A subset of the sample, so its size is only a count when the sample is whole.
      exactCount: !truncated,
    },
    {
      key: 'store',
      label: 'Store',
      scopeName: location?.name ?? null,
      // `location_id` is NULL far more often than not — mail ingestion never sets
      // one, because a store cannot be inferred from an email address. That is
      // not "no tickets", and must never become `?location_id=null`.
      unavailableReason: locationId ? null : 'No store on file for this caller.',
      matches: locationId ? sample.filter((t) => asId(t?.location?.id) === locationId) : [],
      exactCount: !truncated,
    },
    {
      key: 'brand',
      label: 'Brand',
      scopeName: organization?.name ?? null,
      unavailableReason: organizationId ? null : 'No organization on file for this caller.',
      // Defensive rather than decorative: the fetch is already organization-scoped,
      // but this module must not depend on that to be correct.
      matches: organizationId
        ? sample.filter((t) => asId(t?.organization?.id) === organizationId)
        : [],
      // The server counted these, so the number survives truncation intact.
      exactCount: true,
    },
  ];

  return {
    truncated,
    sampleSize: sample.length,
    groups: groups.map((group) => {
      const available = group.unavailableReason === null;
      const count = !available
        ? null
        : group.key === 'brand'
          ? brandTotal
          : group.exactCount
            ? group.matches.length
            : null;
      const tickets = available ? group.matches.slice(0, perGroup) : [];
      return {
        key: group.key,
        label: group.label,
        scopeName: group.scopeName,
        available,
        unavailableReason: group.unavailableReason,
        tickets,
        count,
        // "and N older ones exist" — only ever shown when `count` is a real count.
        moreCount: count === null ? null : Math.max(0, count - tickets.length),
        // The list may be missing older tickets even where the count is exact.
        truncated: available && truncated,
        defaultExpanded: DEFAULT_EXPANDED[group.key],
      };
    }),
  };
}
